import { useEffect, useRef } from "react";
import type { DependencyList, MutableRefObject } from "react";

/** Options for configuring scroll end detection */
interface UseScrollEndOptions {
  /** Distance from bottom (in pixels) to trigger callback. Default: 1 */
  threshold?: number;
  /**
   * Additional dependencies that should trigger re-attachment of the scroll listener.
   * Useful when the container element changes based on other state (e.g., dropdown opening).
   */
  deps?: DependencyList;
}

/**
 * Hook that detects when scrollable containers reach the end and triggers a callback.
 * The callback is triggered only once when scrolling to the bottom, and resets when
 * scrolling away from the bottom.
 *
 * Uses IntersectionObserver for optimal performance by observing a sentinel element
 * placed at the bottom of each container.
 *
 * @param containerRef - Ref to an array of scrollable container elements
 * @param onScrollEnd - Callback function to invoke when scroll reaches the end. Receives the container index.
 * @param options - Configuration options for scroll detection
 *
 * @example
 * ```tsx
 * const scrollRefs = useRef<(HTMLDivElement | null)[]>([]);
 * useScrollEnd(scrollRefs, (columnIndex) => {
 *   console.log(`Column ${columnIndex} reached bottom`);
 * });
 * ```
 *
 * @example
 * ```tsx
 * // With additional dependencies to trigger re-attachment
 * const scrollRefs = useRef<(HTMLDivElement | null)[]>([]);
 * const [isOpen, setIsOpen] = useState(false);
 *
 * useScrollEnd(scrollRefs, handleScrollEnd, { deps: [isOpen] });
 * ```
 */
export function useScrollEnd(
  containerRef: MutableRefObject<(HTMLElement | null)[]>,
  onScrollEnd: ((index: number) => void) | undefined,
  options?: UseScrollEndOptions,
) {
  const hasCalledRef = useRef<boolean[]>([]);

  useEffect(() => {
    const containers = containerRef.current;

    if (!containers || !onScrollEnd) {
      return;
    }

    const observers: IntersectionObserver[] = [];
    const sentinels: HTMLDivElement[] = [];
    const hasCalledFlags: boolean[] = [];

    containers.forEach((container, index) => {
      if (!container) {
        return;
      }

      // Initialize the flag for this container
      hasCalledFlags[index] = false;

      // Create a sentinel element to observe at the bottom of the container
      const sentinel = document.createElement("div");
      sentinel.style.height = "1px";
      sentinel.style.width = "100%";
      sentinel.style.pointerEvents = "none";
      sentinel.setAttribute("data-scroll-sentinel", "true");
      container.appendChild(sentinel);
      sentinels.push(sentinel);

      // Use IntersectionObserver to detect when the sentinel (bottom) is visible
      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;

          if (entry.isIntersecting) {
            // Only trigger if the container is actually scrollable
            const isScrollable = container.scrollHeight > container.clientHeight;

            // Sentinel is visible - we've reached the bottom
            if (!hasCalledFlags[index] && isScrollable) {
              hasCalledFlags[index] = true;
              onScrollEnd(index);
            }
          } else {
            // Sentinel is not visible - we've scrolled away from the bottom
            hasCalledFlags[index] = false;
          }
        },
        {
          root: container,
          rootMargin: `${options?.threshold ?? 1}px`,
          threshold: 0,
        },
      );

      observer.observe(sentinel);
      observers.push(observer);
    });

    // Store the flags in the ref
    hasCalledRef.current = hasCalledFlags;

    return () => {
      observers.forEach((observer) => observer.disconnect());
      sentinels.forEach((sentinel) => {
        if (sentinel.parentNode) {
          sentinel.parentNode.removeChild(sentinel);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, onScrollEnd, options?.threshold, ...(options?.deps ?? [])]);
}
